import { SxProps, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { PixelCrop } from 'react-image-crop';
import { SnackbarSeverityEnum } from '../../enums/snackbar-severity-enum';
import useSnackbar from '../../hooks/useSnackBar';
import { useThemeContext } from '../../theme/ThemeContextProvider';
import { errorHelper } from '../../utils/helper/error-helper';
import setCanvasPreview from '../../utils/setCanvasPreview';
import BaseModal from '../MUIComponents/BaseModal';
import Loader from '../MUIComponents/Loader';
import SnackbarCustom from '../MUIComponents/SnackbarCustom';
import PhotoEditor from '../PhotoEditor/PhotoEditor';

export interface FinalCropData {
  img: HTMLImageElement;
  canvas: HTMLCanvasElement;
  crop: PixelCrop;
}

interface ProfilePhotoEditorModalProps {
  onClose: () => void;
  isOpen: boolean;
  originImgSrc: string;
  setCroppedImage: (file: File) => void;
  setCroppedImagePreviewUrl: (url: string) => void;
}

const ProfilePhotoEditorModal = ({
  onClose,
  isOpen = false,
  originImgSrc,
  setCroppedImage,
  setCroppedImagePreviewUrl,
}: ProfilePhotoEditorModalProps) => {
  const { theme } = useThemeContext();
  const tabletAndBigger = useMediaQuery(theme.breakpoints.up('lg'));
  const [finalCroppedData, setFinalCroppedData] =
    useState<FinalCropData | null>(null);

  const [isUpdatingProfilePhoto, setIsUpdatingProfilePhoto] =
    useState<boolean>(false);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const boxSx: SxProps = {
    width: { xs: '100%', lg: '875px !important' },
    maxHeight: { xs: '80vh', lg: '90dvh' },
    p: { xs: '20px 12px 12px', lg: '36px 36px 48px' },
    position: { xs: 'fixed', lg: 'absolute' },
    top: { lg: '50%' },
    left: { lg: '50%' },
    bottom: { xs: 0, lg: 'unset' },
    borderRadius: { lg: '0 !important' },
    transform: { lg: 'translate(-50%, -50%)' },
    '.content': { m: '48px 0 36px' },
  };

  const closeModal = () => {
    setFinalCroppedData(null);
    onClose();
  };

  const onAccept = async () => {
    try {
      if (
        !finalCroppedData ||
        !finalCroppedData.img ||
        !finalCroppedData.canvas ||
        !finalCroppedData.crop
      ) {
        return;
      }
      setIsUpdatingProfilePhoto(true);

      const { img, crop, canvas } = finalCroppedData;
      setCanvasPreview(img, canvas, crop);

      const dataUrl = canvas.toDataURL('image/png');
      const response = await fetch(dataUrl);

      const blob = await response.blob();

      const file = new File([blob], 'croppedImage.png', { type: 'image/png' });
      setCroppedImage(file);
      setCroppedImagePreviewUrl(URL.createObjectURL(file));
      setIsUpdatingProfilePhoto(false);
      closeModal();
    } catch (error) {
      showSnackbar(
        `Error uploading cropped image: ${errorHelper(error)}`,
        SnackbarSeverityEnum.ERROR
      );
      console.error('Error uploading cropped image:', error);
    } finally {
      setIsUpdatingProfilePhoto(false);
    }
  };

  return (
    <BaseModal
      boxSx={{ ...boxSx }}
      disableEscapeButton
      disableContentMargin
      isOpen={isOpen}
      toggle={closeModal}
      onAccept={onAccept}
      footer={{
        okText: 'select',
        declineText: 'cancel',
        styles: {
          gap: 'unset',
        },
        declineButtonStyles: {
          flex: 'unset !important',
          borderColor: theme.palette.background.darker,
          minWidth: tabletAndBigger ? '220px' : 'unset',
          borderRadius: 0,
        },
        okButtonStyles: {
          flex: 'unset !important',
          minWidth: tabletAndBigger ? '220px' : 'unset',
          borderRadius: 0,
        },
        isOkButtonDisabled:
          !originImgSrc || isUpdatingProfilePhoto ? true : false,
      }}
      header={{ text: 'Add/Edit profile photo' }}
    >
      {isUpdatingProfilePhoto ? (
        <Loader color="primary" />
      ) : (
        <PhotoEditor
          setFinalCroppedData={(data) => setFinalCroppedData(data)}
          imgSrc={originImgSrc}
        />
      )}
      <SnackbarCustom
        open={snackbar.open}
        onClose={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </BaseModal>
  );
};

export default ProfilePhotoEditorModal;
